// Pure, React-free helpers for API-key scopes. Shared by the scope picker and the
// scope badges. The scope grammar mirrors the backend action catalog:
//   "*" | "read" | "write" | "delete" | "<resource>:*" | "<resource>:<action>"

export type QuickPreset = 'full' | 'read' | 'read_write' | 'read_write_delete';

export const QUICK_PRESETS: Record<QuickPreset, string[]> = {
  full: ['*'],
  read: ['read'],
  read_write: ['read', 'write'],
  read_write_delete: ['read', 'write', 'delete'],
};

export const QUICK_PRESET_LABELS: Record<QuickPreset, string> = {
  full: 'Full access',
  read: 'Read-only',
  read_write: 'Read + write',
  read_write_delete: 'Read + write + delete',
};

// Cosmetic verb classification for badge/action coloring (the backend is the real gate).
const READ_ACTIONS = new Set([
  'list', 'get', 'overview', 'forecast', 'history', 'export', 'current-month', 'estimate',
  'console-log', 'action-log', 'list-volumes', 'list-rules', 'available-port',
  'verify-challenge', 'me', 'health', 'capabilities', 'open', 'summary',
]);
const DELETE_ACTIONS = new Set(['delete', 'revoke', 'remove-member', 'remove-rule']);

export function classifyAction(action: string): 'read' | 'write' | 'delete' {
  if (action === '*') return 'write';
  if (READ_ACTIONS.has(action)) return 'read';
  if (DELETE_ACTIONS.has(action)) return 'delete';
  return 'write';
}

/** "managed_postgres" -> "managed postgres" */
export const prettyResource = (resource: string): string => resource.replace(/_/g, ' ');

export interface ScopeBadge {
  token: string;
  label: string;
  kind: 'full' | 'read' | 'write' | 'delete' | 'resource' | 'action';
}

/** Turn a stored scope array into display badges (used on the key list). */
/** DESIGN_EDITING.md E29: the data inside databases is granted by name; "*" does not include it. */
const ROW_DATA_RESOURCES = ['managed_postgres_data', 'managed_postgres_data_edit'];
const isRowData = (token: string) => ROW_DATA_RESOURCES.includes(token.split(':')[0]);

export function parseScopesForDisplay(scopes: string[] | undefined): ScopeBadge[] {
  if (!scopes || scopes.length === 0) return [{ token: '*', label: 'Full access', kind: 'full' }];
  if (scopes.includes('*')) {
    // Full access, plus any row-data scope beside it (which "*" does not include, so it is never hidden behind it)
    return [{ token: '*', label: 'Full access', kind: 'full' }, ...parseScopesForDisplay(scopes.filter((s) => s !== '*' && isRowData(s)))
      .filter((b) => b.kind !== 'full')];
  }
  return scopes.map((token) => {
    if (token === 'read' || token === 'write' || token === 'delete') {
      return { token, label: token, kind: token };
    }
    const [resource, action] = token.split(':');
    if (action === '*') {
      return { token, label: `${prettyResource(resource)}: all`, kind: 'resource' as const };
    }
    return { token, label: `${prettyResource(resource)}: ${action}`, kind: 'action' as const };
  });
}

/** A SQL write by key also needs the data read scope (it can show data through its errors), so say so before it 403s. */
const sqlWriteNote = (scopes: string[]): string => {
  const writes = scopes.some((s) => s === 'managed_postgres_data_edit:sql-write' || s === 'managed_postgres_data_edit:*');
  const reads = scopes.some((s) => s === 'managed_postgres_data:read' || s === 'managed_postgres_data:*');
  return writes && !reads ? ' A SQL write also needs managed postgres data: read.' : '';
};

/** One-line human summary of what a scope set grants (shown live under the picker). */
export function summarizeScopes(scopes: string[]): string {
  if (!scopes || scopes.length === 0) return 'Select at least one capability.';
  if (scopes.includes('*')) {
    const rowData = scopes.filter(isRowData);
    return (rowData.length ? 'Full access to your account, and the data inside your databases.' : 'Full access to your account, not the data inside your databases.') + sqlWriteNote(scopes);
  }
  const verbs = ['read', 'write', 'delete'].filter((v) => scopes.includes(v));
  const granular = scopes.filter((s) => s.includes(':'));
  const parts: string[] = [];
  if (verbs.length) parts.push(`${verbs.join(' + ')} on all resources`);
  if (granular.length) parts.push(`${granular.length} specific ${granular.length === 1 ? 'capability' : 'capabilities'}`);
  return `This key can: ${parts.join(', ')}.${sqlWriteNote(scopes)}`;
}
